import { Area, AreaChart, DotProps, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts';
import { CacDetailsItemResponseDto } from '../../types/response';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

type KpiCacGraphDataPoint = {
  key: string;
  label: string;
  value: number;
};

type RechartsDotProps = DotProps & {
  key?: string;
  index?: number;
};

interface KpiCacGraphProps {
  items: CacDetailsItemResponseDto[];
  className?: string;
}

const formatCurrency = (value: number) => `${value.toLocaleString('de-DE')}€`;

const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload || payload.length === 0) return null;

  const point = payload[0]?.payload as KpiCacGraphDataPoint | undefined;
  if (!point) return null;

  return (
    <div className="rounded-md bg-[#12282A] px-2 py-1 text-xs text-white shadow-sm">
      <span className="font-semibold">{point.label}: </span>
      <span>{formatCurrency(point.value)}</span>
    </div>
  );
};

const HighlightDot = ({ cx, cy, dotKey }: { cx?: number; cy?: number; dotKey?: string }) => {
  if (cx == null || cy == null) return null;

  return (
    <g key={dotKey}>
      <circle cx={cx} cy={cy} r={10} fill="none" stroke="#12282A" strokeWidth={2} opacity={0.2} />
      <circle cx={cx} cy={cy} r={4.5} fill="url(#kpiCacDotGradient)" />
      <circle cx={cx} cy={cy} r={4.5} fill="none" stroke="#12282A" strokeWidth={1.5} />
    </g>
  );
};

const KpiCacGraph = ({ items, className }: KpiCacGraphProps) => {
  const data: KpiCacGraphDataPoint[] = items.map((item) => {
    const monthLabel = MONTH_LABELS[item.month - 1] ?? String(item.month);
    return {
      key: `${item.year}-${item.month}`,
      label: `${monthLabel} ${item.year}`,
      value: Number.isFinite(item.value) ? item.value : 0
    };
  });

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 6, right: 2, left: 2, bottom: 0 }}>
          <defs>
            <linearGradient id="kpiCacFillGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C0DE60" stopOpacity={0.28} />
              <stop offset="100%" stopColor="#5FD5DE" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="kpiCacStrokeGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#5FD5DE" />
              <stop offset="100%" stopColor="#B9D95F" />
            </linearGradient>
            <linearGradient id="kpiCacDotGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C0DE60" />
              <stop offset="100%" stopColor="#5FD5DE" />
            </linearGradient>
          </defs>

          <Area
            type="monotone"
            dataKey="value"
            stroke="url(#kpiCacStrokeGradient)"
            strokeWidth={2.5}
            fillOpacity={1}
            fill="url(#kpiCacFillGradient)"
            dot={(props: RechartsDotProps) => {
              const { key, index, cx, cy } = props;
              if (index !== data.length - 1) return <g key={key} />;
              return <HighlightDot key={key} cx={cx} cy={cy} dotKey={key} />;
            }}
            activeDot={(props: RechartsDotProps) => {
              const { key, cx, cy } = props;
              return <HighlightDot key={key} cx={cx} cy={cy} dotKey={key} />;
            }}
          />
          <Tooltip content={<CustomTooltip />} cursor={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default KpiCacGraph;
